import { get, post } from "./api-client";
import type {
  AdminDashboardData, HealthStatus, ConfigVersion, RewardRule,
  OutboxEvent, AuditEntry, PlacementCandidateAdmin, TreeNodeAdmin,
} from "@/types/admin";

import type {
  ReferralCommissionTransaction, ReferralRuleStatus, ReferralRuleVersion,
  ReferralSummary, ReferralTreeResponse,
} from "@/types/referral";

export const AdminAPI = {
  dashboard: () => get<AdminDashboardData>("/admin/dashboard"),
  health:    () => get<HealthStatus>("/admin/health"),

  // Configuration
  listConfigVersions: () => get<ConfigVersion[]>("/admin/configuration/versions"),
  publishConfig: (values: Record<string, unknown>, description: string) =>
    post<{ version: number; id: number }>(
      "/admin/configuration/publish",
      { values, description },
    ),

   getWithdrawalSettings: () => get<any>("/admin/withdrawals/settings"),
  updateWithdrawalSettings: (data: any) => post<void>("/admin/withdrawals/settings", data), 

  // Reward rules
  listRewardRules: () => get<RewardRule[]>("/admin/reward-rules"),
  createRewardRule: (data: Partial<RewardRule> & {
    formula_type: string; formula_config: Record<string, unknown>;
  }) => post<RewardRule>("/admin/reward-rules", data),

  // Adjustments
  adjustQualification: (data: {
    user_id: string; delta: number; reason: string; idempotency_key: string;
  }) => post<void>("/admin/adjust/qualification", data),
  adjustWallet: (data: {
    user_id: string; delta: string; reason: string; idempotency_key: string;
  }) => post<void>("/admin/adjust/wallet", data),

  // DLQ
  retryNotification: (id: number) =>
    post<{ outcome: string }>(`/admin/notifications/${id}/retry`),
  retryOutbox: (event_id: string) =>
    post<{ outcome: string }>(`/admin/outbox/${event_id}/retry`),

  // Tree
  treeSnapshot: () => get<TreeNodeAdmin[]>("/admin/placement/tree"),
  placementQueue: () => get<PlacementCandidateAdmin[]>("/admin/placement/queue"),

  // Outbox / audit
  listOutbox: (status?: string) =>
    get<OutboxEvent[]>("/admin/outbox", status ? { status } : {}),
  listAudit: (params?: Record<string, string>) =>
    get<AuditEntry[]>("/admin/audit", params),

  updateUserPhone: (userId: string, mobile: string) => 
    post<void>(`/admin/users/${userId}/phone`, { mobile }),
    
  resetUserPassword: (userId: string, password: string) => 
    post<void>(`/admin/users/${userId}/password`, { password }),
};


// Append to `adminApi` object:
export const referralApi = {
  // Customer
  myTree:        (generation?: number) =>
    get<ReferralTreeResponse>("/referrals/tree",
                                generation ? { generation } : undefined),
  mySummary:     () => get<ReferralSummary>("/referrals/summary"),
  myCommissions: () => get<ReferralCommissionTransaction[]>("/referrals/commissions"),

  attachSponsor: (sponsor_user_id: string) =>
    post("/auth/attach-sponsor", { sponsor_user_id },
         { idempotencyKey: newIdempotencyKey() }),

  // Admin
  adminStatus:   () => get<ReferralRuleStatus>("/admin/referrals/configuration"),
  adminHistory:  () => get<ReferralRuleVersion[]>("/admin/referrals/configuration/history"),
  adminPublish:  (rate: string, effective_from?: string) =>
    post<ReferralRuleVersion>("/admin/referrals/configuration/publish",
                                { rate, effective_from },
                                { idempotencyKey: newIdempotencyKey() }),
  adminToggle:   (enabled: boolean) =>
    post("/admin/referrals/configuration/toggle", { enabled },
         { idempotencyKey: newIdempotencyKey() }),
  adminCommissions: (params?: Record<string, unknown>) =>
    get<ReferralCommissionTransaction[]>("/admin/referrals/commissions", params),
};

function newIdempotencyKey(): string | undefined {
  throw new Error("Function not implemented.");
}

